import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useToast } from "@/hooks/use-toast";
import { Header } from "@/components/Header";
import { BackButton } from "@/components/onboarding/BackButton";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Send, Loader2, ArrowRight, CheckCircle2, AlertCircle } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";

const TelegramConnect = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();
  const [isConnecting, setIsConnecting] = useState(false);
  const [connectionAttempted, setConnectionAttempted] = useState(false);
  const [connectionComplete, setConnectionComplete] = useState(false);
  const [pollingTimeout, setPollingTimeout] = useState<NodeJS.Timeout | null>(null);

  useEffect(() => {
    checkTelegramConnection();
    
    return () => {
      if (pollingTimeout) {
        clearTimeout(pollingTimeout);
      }
    };
  }, []);

  useEffect(() => {
    checkPreferredMessaging();
  }, []);

  const extractLastPathSegment = (path: string) => {
    const segments = path.replace(/^\//, '').split('/');
    return segments[segments.length - 1];
  };

  const checkPreferredMessaging = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;
      
      const { data: profile } = await supabase
        .from('profiles')
        .select('preferred_messaging')
        .eq('id', user.id)
        .single();
      
      if (profile?.preferred_messaging === 'whatsapp') {
        navigate('/add-services');
      }
    } catch (error) {
      console.error('Error checking preferred messaging:', error);
    }
  };

  const checkTelegramConnection = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;
      
      const { data: profile } = await supabase
        .from('profiles')
        .select('telegram_chat_id')
        .eq('id', user.id)
        .single();
      
      if (profile?.telegram_chat_id) {
        setConnectionComplete(true);
        if (pollingTimeout) {
          clearTimeout(pollingTimeout);
          setPollingTimeout(null);
        }
      }
    } catch (error) {
      console.error('Error checking telegram connection:', error);
    }
  };

  const connectTelegram = async () => {
    setIsConnecting(true);
    
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('No user found');
      
      const currentPath = location.pathname;
      const lastPathSegment = extractLastPathSegment(currentPath);
      
      const payload = `${user.id}${lastPathSegment}&telegramconnect`;
      const deepLink = `https://t.me/GebeyaMeronBot?start=${payload}`;
      
      const { error: connectionError } = await supabase
        .from('user_telegram_connections')
        .insert({
          user_id: user.id,
          connection_token: user.id,
          status: 'pending'
        });
      
      if (connectionError) {
        console.error("Error creating telegram connection record:", connectionError);
        throw connectionError;
      }
      
      const newWindow = window.open(deepLink, '_blank');
      
      if (!newWindow || newWindow.closed || typeof newWindow.closed === 'undefined') {
        window.location.href = deepLink;
      }
      
      setConnectionAttempted(true);
      toast({
        title: "Telegram Bot",
        description: "Opening Telegram. Follow the instructions to complete the connection.",
      });

      pollForConnection();
    } catch (error) {
      console.error('Error connecting to Telegram:', error);
      toast({
        title: "Error",
        description: "Failed to connect to Telegram. Please try again later.",
        variant: "destructive",
      });
    } finally {
      setIsConnecting(false);
    }
  };

  const pollForConnection = () => {
    let attempts = 0;
    const maxAttempts = 20;
    const pollInterval = 3000;
    
    const checkConnection = async () => {
      attempts++;
      console.log(`Checking Telegram connection (attempt ${attempts}/${maxAttempts})...`);
      
      await checkTelegramConnection();
      
      if (connectionComplete) {
        console.log("Telegram connection successful!");
        toast({
          title: "Success",
          description: "Your Telegram account is now connected!",
        });
        return;
      }
      
      if (attempts >= maxAttempts) {
        console.log("Max polling attempts reached");
        return;
      }
      
      const timeout = setTimeout(checkConnection, pollInterval);
      setPollingTimeout(timeout);
    };
    
    const timeout = setTimeout(checkConnection, pollInterval);
    setPollingTimeout(timeout);
  };

  const skipTelegramConnect = () => {
    navigate('/add-services');
  };

  const continueToNextStep = () => {
    navigate('/add-services');
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-pink-50 to-white">
      <Header />
      <BackButton onClick={() => navigate('/onboarding')} className="md:fixed" />
      <div className="container mx-auto px-4 py-24 flex flex-col items-center justify-center">
        <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8">
          <h1 className="text-3xl font-bold text-gebeya-pink mb-3">
            Connect Telegram
          </h1>
          <p className="text-base text-gray-600 mb-6">
            Connecting Telegram enables seamless notification delivery for service requests
          </p>

          <div className="mb-6 border-l-4 border-blue-400 bg-blue-50 p-4 rounded">
            <div className="flex items-start">
              <AlertCircle className="h-5 w-5 text-blue-500 mr-2 mt-0.5" />
              <div>
                <h3 className="font-medium text-blue-700">How to connect:</h3>
                <ol className="list-decimal list-inside mt-2 space-y-1.5 text-blue-700">
                  <li>Click the "Connect to Telegram Assistant" button below</li>
                  <li>A Telegram link will open in a new tab</li>
                  <li>Find and open the <strong>Gebeya Meron Bot</strong></li>
                  <li>Click the <strong className="px-2 py-1 bg-blue-100 rounded text-blue-800 font-mono">START</strong> button or type <strong className="px-2 py-1 bg-blue-100 rounded text-blue-800 font-mono">/start</strong></li>
                  <li>After the bot responds, return back to this page (it should update automatically)</li>
                </ol>
              </div>
            </div>
          </div>

          <div className="space-y-6 py-4">
            {connectionComplete ? (
              <Alert className="bg-green-50 border-green-100">
                <CheckCircle2 className="h-5 w-5 text-green-500 mr-2" />
                <AlertTitle className="text-green-700 font-medium">Successfully Connected</AlertTitle>
                <AlertDescription className="text-green-700">
                  Your Telegram account is connected and ready to receive notifications. 
                  You can now continue to the next step.
                </AlertDescription>
              </Alert>
            ) : connectionAttempted ? (
              <Alert className="bg-yellow-50 border-yellow-100">
                <AlertTitle className="text-yellow-700 font-medium">Connection Pending</AlertTitle>
                <AlertDescription className="text-yellow-700">
                  We're waiting for you to connect with our bot on Telegram. 
                  <ol className="list-decimal list-inside mt-2 space-y-2">
                    <li>Open the Telegram link sent to you</li>
                    <li>Find the Gebeya Meron Bot</li>
                    <li>Click the START or /start button to complete the connection</li>
                  </ol>
                  <div className="mt-3 flex justify-center">
                    <img 
                      src="/lovable-uploads/4803bfeb-c3c5-4783-8989-f4e30131f19c.png" 
                      alt="Telegram" 
                      className="h-24 opacity-80" 
                    />
                  </div>
                </AlertDescription>
              </Alert>
            ) : (
              <div className="text-center py-2">
                <p className="mb-4">
                  For the best experience with Telegram notifications, connect your account now.
                </p>
                <div className="flex justify-center mb-4">
                  <img 
                    src="/lovable-uploads/4803bfeb-c3c5-4783-8989-f4e30131f19c.png" 
                    alt="Telegram" 
                    className="h-24 opacity-80" 
                  />
                </div>
              </div>
            )}

            <div className="flex flex-col gap-3">
              {connectionComplete ? (
                <Button 
                  variant="default" 
                  onClick={continueToNextStep}
                  className="bg-gradient-to-r from-gebeya-pink to-gebeya-orange hover:opacity-90 text-white w-full"
                >
                  <CheckCircle2 className="h-4 w-4 mr-2" />
                  Continue to next step
                </Button>
              ) : (
                <>
                  <Button 
                    variant="default" 
                    onClick={connectTelegram}
                    disabled={isConnecting || connectionAttempted}
                    className="bg-gradient-to-r from-gebeya-pink to-gebeya-orange hover:opacity-90 text-white w-full"
                  >
                    {isConnecting ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Connecting...
                      </>
                    ) : connectionAttempted ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Waiting for connection...
                      </>
                    ) : (
                      <>
                        <Send className="h-4 w-4 mr-2" />
                        Connect to Telegram Assistant
                      </>
                    )}
                  </Button>
                  
                  {connectionAttempted && (
                    <Button
                      variant="outline"
                      onClick={connectTelegram}
                      className="w-full"
                    >
                      <Send className="h-4 w-4 mr-2" />
                      Open Telegram link again
                    </Button>
                  )}
                  
                  <Button
                    variant="outline"
                    onClick={skipTelegramConnect}
                    className="w-full"
                  >
                    Skip for now
                    <ArrowRight className="h-4 w-4 ml-2" />
                  </Button>
                </>
              )}
            </div>

            <p className="text-xs text-gray-500 text-center mt-2">
              You can always connect Telegram later in the Settings page
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TelegramConnect;
